import { useStore } from '../state/store';
import { Slider, Toggle, Select } from './controls';
import type { Step } from '@eip/engine';
import { Section } from './Section';

const TITLES: Record<Step['kind'], string> = { halftone: 'Halftone soft areas', knockout: 'Knock out garment colour', despeckle: 'Remove stray specks', trim: 'Trim to content', resize: 'Resize to print width', harden: 'Harden alpha', defringe: 'Defringe' };

export function PipelinePanel() {
  const steps = useStore((s) => s.steps);
  const updateStep = useStore((s) => s.updateStep);
  const setStepEnabled = useStore((s) => s.setStepEnabled);
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  const info = (i: number) => doc?.run?.steps.find((p) => p.index === i);
  return (
    <Section id="pipeline" title="Pipeline">
      {steps.map((step, i) => (
        <div key={step.id} className={`step${step.enabled ? '' : ' off'}`}>
          <div className="row">
            <Toggle label={TITLES[step.kind]} checked={step.enabled} onChange={(v) => setStepEnabled(i, v)} />
            {/* The word, not the number: "68 ms" beside a step told the shop
                nothing and crowded out what does. The timing stays on the
                hover and in `data-ms` for anyone measuring. */}
            {info(i) && <span className="muted small right" data-ms={info(i)!.ms.toFixed(0)} title={`Ran in ${info(i)!.ms.toFixed(0)} ms`}>Done</span>}
          </div>
          {step.enabled && step.kind === 'knockout' && (<>
            <Slider label="Tolerance" value={step.params.tolerance} min={0} max={120} onChange={(v) => updateStep(i, { tolerance: v })} />
            <Toggle label="Only regions touching the edge" checked={!!step.params.edgeConnectedOnly} onChange={(v) => updateStep(i, { edgeConnectedOnly: v })} />
          </>)}
          {step.enabled && step.kind === 'despeckle' && (<>
            <Slider label="Min island area" hint="Specks smaller than this are removed" value={step.params.minArea} min={0} max={400} unit="px²" onChange={(v) => updateStep(i, { minArea: v })} />
            <Toggle label="Protect texture near the art" checked={step.params.protectDistressed !== false} onChange={(v) => updateStep(i, { protectDistressed: v })} />
            <Slider label="Protect radius" hint="Keeps detail this close to the art untouched" value={step.params.protectRadius ?? 12} min={0} max={60} unit="px" onChange={(v) => updateStep(i, { protectRadius: v })} />
            {info(i)?.info && <div className="muted small">removed {info(i)!.info!.removed} islands · kept {info(i)!.info!.kept}</div>}
          </>)}
          {step.enabled && step.kind === 'halftone' && (<>
            <Slider label="LPI" hint="Halftone dot lines per inch" value={step.params.lpi} min={15} max={65} onChange={(v) => updateStep(i, { lpi: v })} />
            <Slider label="Angle" value={step.params.angle} min={0} max={90} step={0.5} unit="°" onChange={(v) => updateStep(i, { angle: v })} />
            <Select label="Dot" hint="Shape of the halftone dot" value={step.params.shape} options={['round', 'ellipse', 'line', 'square', 'diamond'].map((s) => ({ value: s, label: s }))} onChange={(v) => updateStep(i, { shape: v })} />
            <Slider label="Min dot" value={Math.round((step.params.minDot ?? 0.02) * 100)} min={0} max={20} unit="%" onChange={(v) => updateStep(i, { minDot: v / 100 })} />
            <Slider label="Max dot" value={Math.round((step.params.maxDot ?? 0.95) * 100)} min={80} max={100} unit="%" onChange={(v) => updateStep(i, { maxDot: v / 100 })} />
          </>)}
          {step.enabled && step.kind === 'trim' && (
            <Slider label="Padding" value={step.params.padding} min={0} max={200} unit="px" onChange={(v) => updateStep(i, { padding: v })} />
          )}
          {/* Read-only on purpose: the bottom settings bar owns print width and
              DPI (it is what `inspect` and the export filename read), and a
              second writable copy here let the two disagree — the output was
              resized to the card's width while the inspector judged it at the
              bar's. The step still stores widthIn/dpi, so serializeSteps /
              parseSteps are unchanged. */}
          {step.enabled && step.kind === 'resize' && (
            <div className="muted small">Width {step.params.widthIn} in · {Math.round(step.params.dpi)} dpi — set in the bar below</div>
          )}
        </div>
      ))}
      {/* The output's size and density are the run's RESULT, so they live in the
          panel's pinned footer now (`PanelFooter`) rather than at the end of a
          list the shop has to scroll to reach. */}
    </Section>
  );
}
